/**
 * news service — fetches and normalises F1 news from multiple RSS feeds.
 * -------------------------------------------------------------------------
 * Data flow:
 *
 *   RSS Feeds (Tier 1 & 2)  →  rss2json proxy  →  this service  →  React state
 *
 * The shape returned by `fetchNews()` matches the NewsArticle contract.
 * Uses in-memory caching with stale-while-revalidate to respect rate limits.
 */

import { getEnabledSources, FETCH_CONFIG } from '../data/newsSources.js';
import {
  fetchAndNormalizeFeed,
  deduplicateArticles,
  sortArticles,
  filterByCategory,
  reconcileEstimatedDates,
} from '../utils/newsParser.js';
import { fetchWithCache, createCacheKey } from '../utils/cache.js';

const CACHE_KEY = 'f1-news-feed';
const FETCH_TTL_MS = FETCH_CONFIG.defaultTtlMs;

let cachedFullFeed = null;
let lastFullFetch = 0;

/**
 * fetchNews — public entry point for the News screen.
 *
 * @param {object} options
 * @param {string} [options.category] one of NEWS_CATEGORIES ids; defaults to 'all'
 * @param {AbortSignal} [options.signal] standard fetch-style abort signal
 * @param {boolean} [options.forceRefresh] bypass cache
 * @returns {Promise<NewsArticle[]>} normalised feed, freshest first
 */
export async function fetchNews({ category = 'all', signal, forceRefresh = false } = {}) {
  const sources = getEnabledSources();

  const fetcher = async () => {
    const results = await Promise.allSettled(
      sources.map((source) =>
        fetchWithCache(
          createCacheKey('feed', source.id),
          // Unwrap to just the article array — fetchWithCache already adds
          // its own { data, fromCache, ... } envelope, so caching the
          // { source, data } shape here would double-nest `.data`.
          () => fetchAndNormalizeFeed(source, signal).then((r) => r.data),
          { ttl: FETCH_TTL_MS, forceRefresh }
        )
      )
    );

    const allArticles = results
      .filter((r) => r.status === 'fulfilled')
      .flatMap((r) => r.value.data);

    const errors = results
      .filter((r) => r.status === 'rejected')
      .map((r) => r.reason);

    if (errors.length === results.length && allArticles.length === 0) {
      throw new Error(`All feeds failed: ${errors.map((e) => e.message).join('; ')}`);
    }

    // Re-anchor synthesised dates against the real ones now that every feed is
    // merged — see reconcileEstimatedDates. Must run before sorting.
    const reconciled = reconcileEstimatedDates(allArticles);
    const deduped = deduplicateArticles(reconciled);
    const sorted = sortArticles(deduped);

    cachedFullFeed = sorted;
    lastFullFetch = Date.now();

    return sorted;
  };

  const { data, fromCache } = await fetchWithCache(
    CACHE_KEY,
    fetcher,
    { ttl: FETCH_TTL_MS, forceRefresh }
  );

  const filtered = filterByCategory(data, category);

  return {
    articles: filtered,
    meta: {
      fromCache,
      sourceCount: sources.length,
      totalArticles: data.length,
      fetchedAt: new Date(lastFullFetch).toISOString(),
    },
  };
}

/**
 * fetchArticle — single-article lookup by ID.
 * Note: with RSS feeds, IDs are generated. For deep links, we search by ID.
 *
 * @param {string} id
 * @returns {Promise<NewsArticle | null>}
 */
export async function fetchArticle(id) {
  if (cachedFullFeed) {
    return cachedFullFeed.find((a) => a.id === id) ?? null;
  }

  const { articles } = await fetchNews({ category: 'all' });
  return articles.find((a) => a.id === id) ?? null;
}

/**
 * getFeedStatus — returns cache/feed health info for debugging.
 */
export function getFeedStatus() {
  return {
    hasCachedFeed: !!cachedFullFeed,
    lastFetch: lastFullFetch ? new Date(lastFullFetch).toISOString() : null,
    cacheAgeMs: lastFullFetch ? Date.now() - lastFullFetch : null,
    articleCount: cachedFullFeed?.length ?? 0,
  };
}

/**
 * clearFeedCache — forces a fresh fetch on next call.
 */
export function clearFeedCache() {
  cachedFullFeed = null;
  lastFullFetch = 0;
}